import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { productListApiService } from "../../../service/admin/productApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import "./supplierDetail.css";

// Check if text contains Chinese characters
const isChinese = (text) => /[\u4e00-\u9fff]/.test(text);

// Translation cache
const translationCache = {};

// Translate Chinese text to English
const translateToEnglish = async (text) => {
  if (!text || !isChinese(text)) return null;
  if (translationCache[text]) return translationCache[text];
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=zh-CN&tl=en&dt=t&q=${encodeURIComponent(text)}`
    );
    const data = await res.json();
    const translated = data?.[0]?.map(item => item[0]).join('') || null;
    if (translated) translationCache[text] = translated;
    return translated;
  } catch (err) {
    return null;
  }
};

export default function SupplierDetail() {
  const { sellerOpenId } = useParams();
  const loader = useLoader();
  const message = useMessage();

  const [supplier, setSupplier] = useState(null);
  const [englishName, setEnglishName] = useState("");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderSummary, setOrderSummary] = useState(null);
  const [productPage, setProductPage] = useState(1);
  const [productPages, setProductPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [orderPage, setOrderPage] = useState(1);
  const [orderPages, setOrderPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [productLoading, setProductLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const productListRef = useRef(null);
  const orderListRef = useRef(null);

  const formatNumber = (num) => num ? num.toLocaleString("en-IN") : "0";


  const fetchSupplierDetails = async () => {
    loader.showInlineLoader("Loading supplier...", "medium");
    try {
      const res = await productListApiService.getSupplierDetails(sellerOpenId);
      loader.hideLoader();
      if (res?.status?.code === 200 && res.data) {
        setSupplier(res.data);
        if (res.data.companyName && isChinese(res.data.companyName)) {
          translateToEnglish(res.data.companyName).then(setEnglishName);
        }
      } else {
        message.showToast("Supplier not found", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Server error", ToastTypes.ERROR);
    }
  };

  const fetchProducts = async (pageNum = 1) => {
    setProductLoading(true);
    try {
      const res = await productListApiService.getSupplierProducts(sellerOpenId, `?page=${pageNum}&limit=20`);
      setProductLoading(false);
      if (res?.status?.code === 200) {
        setProducts(res.data.products || []);
        setProductPages({ currentPage: res.data.currentPage, totalPages: res.data.totalPages, totalItems: res.data.totalProducts });
      }
    } catch (err) {
      setProductLoading(false);
    }
  };

  const fetchOrders = async (pageNum = 1) => {
    setOrderLoading(true);
    try {
      const res = await productListApiService.getSupplierOrderDetails(sellerOpenId, `?page=${pageNum}&limit=20`);
      setOrderLoading(false);
      if (res?.status?.code === 200) {
        setOrders(res.data.orderDetails || []);
        setOrderPages({ currentPage: res.data.currentPage, totalPages: res.data.totalPages, totalItems: res.data.totalItems });
        setOrderSummary(res.data.summary || null);
      }
    } catch (err) {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    if (sellerOpenId) {
      fetchSupplierDetails();
      fetchProducts(1);
      fetchOrders(1);
    }
  }, [sellerOpenId]);

  const handleProductPageChange = (newPage) => {
    setProductPage(newPage);
    fetchProducts(newPage);
    if (productListRef.current) productListRef.current.scrollTop = 0;
  };

  const handleOrderPageChange = (newPage) => {
    setOrderPage(newPage);
    fetchOrders(newPage);
    if (orderListRef.current) orderListRef.current.scrollTop = 0;
  };

  const getScoreClass = (score) => {
    if (!score) return "na";
    const num = parseFloat(score);
    if (num >= 4.5) return "excellent";
    if (num >= 4.0) return "good";
    if (num >= 3.5) return "average";
    return "poor";
  };

  const renderScoreBar = (label, score, maxScore = 5) => {
    const numScore = parseFloat(score) || 0;
    const percent = (numScore / maxScore) * 100;
    return (
      <div className="score-bar-item">
        <span className="score-label">{label}</span>
        <div className="score-bar-track">
          <div className="score-bar-fill" style={{ width: `${percent}%` }} />
        </div>
        <span className={`score-value ${getScoreClass(score)}`}>{numScore.toFixed(1)}</span>
      </div>
    );
  };

  if (!supplier) {
    return (
      <div className="supplier-detail-page">
        <div className="sd-loading">Loading supplier details...</div>
      </div>
    );
  }

  const sellerData = supplier.sellerDataInfo || {};


  return (
    <div className="supplier-detail-page">
      {/* Header Banner */}
      <div className="sd-header-banner">
        <div className="sd-header-content">
          <div className="sd-company-info">
            <div className="sd-company-logo">
              <span className="logo-text">{supplier.companyName?.charAt(0) || "S"}</span>
            </div>
            <div className="sd-company-details">
              <h1 className="sd-company-name">{supplier.companyName || "Unknown Supplier"}</h1>
              {englishName && <p className="sd-company-name-en">{englishName}</p>}
              <p className="sd-seller-id">Seller ID: {supplier.sellerOpenId}</p>
            </div>
          </div>
          <div className="sd-header-stats">
            <div className="sd-stat">
              <span className="sd-stat-value">{formatNumber(supplier.totalProducts)}</span>
              <span className="sd-stat-label">Products</span>
            </div>
            <div className="sd-stat">
              <span className="sd-stat-value green">₹{formatNumber(supplier.actualSalesAmount || 0)}</span>
              <span className="sd-stat-label">Total Sales</span>
            </div>
            <div className="sd-stat">
              <span className="sd-stat-value">{formatNumber(supplier.actualOrderCount || 0)}</span>
              <span className="sd-stat-label">Orders</span>
            </div>
            <div className="sd-stat">
              <span className="sd-stat-value">{formatNumber(supplier.actualQuantitySold || 0)}</span>
              <span className="sd-stat-label">Qty Sold</span>
            </div>
            <div className="sd-stat">
              <span className="sd-stat-value">{formatNumber(supplier.skusWithOrdersCount || 0)}</span>
              <span className="sd-stat-label">SKUs Sold</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="sd-main-content">
        {/* Left Sidebar - Seller Info */}
        <div className="sd-sidebar">
          {/* Trade Score Card */}
          <div className="sd-card sd-score-card">
            <div className="sd-card-header">
              <span className="sd-card-title">Seller Ratings</span>
              <span className={`sd-trade-score ${getScoreClass(supplier.tradeScore)}`}>
                {supplier.tradeScore?.toFixed(1) || "-"}
              </span>
            </div>
            <div className="sd-card-body">
              {renderScoreBar("Composite Service", sellerData.compositeServiceScore)}
              {renderScoreBar("Logistics Experience", sellerData.logisticsExperienceScore)}
              {renderScoreBar("Dispute Complaint", sellerData.disputeComplaintScore)}
              {renderScoreBar("Offer Experience", sellerData.offerExperienceScore)}
              {renderScoreBar("After Sales", sellerData.afterSalesExperienceScore)}
              {renderScoreBar("Consulting", sellerData.consultingExperienceScore)}
              {sellerData.repeatPurchasePercent && (
                <div className="sd-repeat-rate">
                  <span>Repeat Purchase Rate</span>
                  <span className="rate-value">{(parseFloat(sellerData.repeatPurchasePercent) * 100).toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Company Info Card */}
          <div className="sd-card">
            <div className="sd-card-header">
              <span className="sd-card-title">Company Info</span>
            </div>
            <div className="sd-card-body sd-info-list">
              {supplier.location && (
                <div className="sd-info-row">
                  <span className="sd-info-label">Location</span>
                  <span className="sd-info-value">{supplier.location}</span>
                </div>
              )}
              {supplier.topCategoryId && (
                <div className="sd-info-row">
                  <span className="sd-info-label">Top Category</span>
                  <span className="sd-info-value">{supplier.topCategoryId}</span>
                </div>
              )}
              <div className="sd-info-row">
                <span className="sd-info-label">Total Products</span>
                <span className="sd-info-value">{formatNumber(supplier.totalProducts)}</span>
              </div>
              <div className="sd-info-row">
                <span className="sd-info-label">Verified Products</span>
                <span className="sd-info-value verified">{formatNumber(supplier.verifiedProducts || 0)}</span>
              </div>
              <div className="sd-info-row">
                <span className="sd-info-label">Active Products</span>
                <span className="sd-info-value active">{formatNumber(supplier.activeProducts || 0)}</span>
              </div>
              {sellerData.tradeMedalLevel && (
                <div className="sd-info-row">
                  <span className="sd-info-label">Trade Medal</span>
                  <span className="sd-info-value medal">Level {sellerData.tradeMedalLevel}</span>
                </div>
              )}
            </div>
          </div>

          {/* Tags Card */}
          {supplier.tagInfoList && supplier.tagInfoList.length > 0 && (
            <div className="sd-card">
              <div className="sd-card-header">
                <span className="sd-card-title">Seller Tags</span>
              </div>
              <div className="sd-card-body sd-tags-list">
                {supplier.tagInfoList.map((tag, idx) => {
                  let tagDisplay = "";
                  if (typeof tag === "string") tagDisplay = tag;
                  else if (tag?.key && tag?.value !== undefined) {
                    if (tag.value === true) tagDisplay = tag.key;
                    else if (tag.value !== false) tagDisplay = `${tag.key}: ${tag.value}`;
                  }
                  if (!tagDisplay) return null;
                  return <span key={idx} className="sd-tag">{tagDisplay}</span>;
                })}
              </div>
            </div>
          )}
        </div>


        {/* Right Content - Products/Orders */}
        <div className="sd-content">
          {/* Tabs */}
          <div className="sd-tabs">
            <button className={`sd-tab ${activeTab === "products" ? "active" : ""}`} onClick={() => setActiveTab("products")}>
              Products <span className="tab-count">{formatNumber(productPages.totalItems)}</span>
            </button>
            <button className={`sd-tab ${activeTab === "orders" ? "active" : ""}`} onClick={() => setActiveTab("orders")}>
              Orders <span className="tab-count">{formatNumber(orderPages.totalItems)}</span>
            </button>
          </div>

          {/* Products Tab */}
          {activeTab === "products" && (
            <div className="sd-products-section">
              <div className="sd-section-header">
                <span>Showing {products.length} of {formatNumber(productPages.totalItems)} products</span>
                {productPages.totalPages > 1 && (
                  <div className="sd-pagination">
                    <button disabled={productPage <= 1} onClick={() => handleProductPageChange(productPage - 1)}>‹ Prev</button>
                    <span>Page {productPage} of {productPages.totalPages}</span>
                    <button disabled={productPage >= productPages.totalPages} onClick={() => handleProductPageChange(productPage + 1)}>Next ›</button>
                  </div>
                )}
              </div>
              
              <div className="sd-products-grid" ref={productListRef}>
                {productLoading ? (
                  <div className="sd-loading">Loading products...</div>
                ) : products.length > 0 ? (
                  products.map((p, idx) => (
                    <div 
                      key={p._id || idx} 
                      className="sd-product-card"
                      onClick={() => window.open(`/product-details?id=${p._id}`, '_blank')}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="sd-product-image">
                        {p.imgUrl?.[0] ? (
                          <img src={p.imgUrl[0]} alt={p.title} referrerPolicy="no-referrer" />
                        ) : (
                          <div className="sd-no-image">No Image</div>
                        )}
                      </div>
                      <div className="sd-product-info">
                        <h3 className="sd-product-title" title={p.title}>{p.title?.substring(0, 60)}{p.title?.length > 60 ? "..." : ""}</h3>
                        <div className="sd-product-meta">
                          <span className="sd-product-sku">SKU: {p.sku}</span>
                        </div>
                        <div className="sd-product-pricing">
                          <span className="sd-product-price">₹{formatNumber(p.sp || 0)}</span>
                          {p.orderSalesAmount > 0 && (
                            <span className="sd-product-sales">Sales: ₹{formatNumber(p.orderSalesAmount)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="sd-no-data">No products found</div>
                )}
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div className="sd-orders-section">
              {orderSummary && (
                <div className="sd-order-summary">
                  <div className="sd-summary-item">
                    <span className="sd-summary-label">Total Orders</span>
                    <span className="sd-summary-value">{formatNumber(orderSummary.totalOrders)}</span>
                  </div>
                  <div className="sd-summary-item">
                    <span className="sd-summary-label">Total Quantity</span>
                    <span className="sd-summary-value">{formatNumber(orderSummary.totalQuantity)}</span>
                  </div>
                  <div className="sd-summary-item">
                    <span className="sd-summary-label">Total Sales</span>
                    <span className="sd-summary-value green">₹{formatNumber(orderSummary.totalSalesAmount)}</span>
                  </div>
                </div>
              )}

              <div className="sd-section-header">
                <span>SKUs with Orders ({formatNumber(orderPages.totalItems)})</span>
                {orderPages.totalPages > 1 && (
                  <div className="sd-pagination">
                    <button disabled={orderPage <= 1} onClick={() => handleOrderPageChange(orderPage - 1)}>‹ Prev</button>
                    <span>Page {orderPage} of {orderPages.totalPages}</span>
                    <button disabled={orderPage >= orderPages.totalPages} onClick={() => handleOrderPageChange(orderPage + 1)}>Next ›</button>
                  </div>
                )}
              </div>

              <div className="sd-orders-table" ref={orderListRef}>
                {orderLoading ? (
                  <div className="sd-loading">Loading orders...</div>
                ) : orders.length > 0 ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Product</th>
                        <th>Offer ID</th>
                        <th>Variant SKU</th>
                        <th>Orders</th>
                        <th>Qty</th>
                        <th>Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o, idx) => (
                        <tr key={o.sku || idx}>
                          <td>
                            <div className="sd-order-img">
                              {o.productImage ? <img src={o.productImage} alt="" referrerPolicy="no-referrer" /> : "-"}
                            </div>
                          </td>
                          <td><span className="sd-order-title">{o.productTitle?.substring(0, 40)}...</span></td>
                          <td>{o.offerId || "-"}</td>
                          <td>{o.variantSkuId || "-"}</td>
                          <td>{formatNumber(o.totalOrders)}</td>
                          <td>{formatNumber(o.totalQuantity)}</td>
                          <td className="green">₹{formatNumber(o.totalSalesAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="sd-no-data">No orders found</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
